import { isEmpty } from '@/utils/isEmpty/isEmpty';

const cachekey = '20170802';

/*
{
    onBuildRequest: null,
    onSendRequest: null,
    onHandleResponse: null,
}
*/
let perfLoggerRequestHooks: any = null;

type HandleResponseParams<ResponsePayload, ReturnedPayload> = {
    err?: any;
    manuallyHandledErrorCodes?: number[];
    reject: Function;
    request?: { url: string }; // For backward compatibility with existing API calls
    requestUrl?: string;
    resolve: Function;
    response: any;
    success404?: boolean;
    transform?: (payload: ResponsePayload) => ReturnedPayload;
};

export const handleResponseWithNon200Errors = <ResponsePayload, ReturnedPayload>({
    err,
    manuallyHandledErrorCodes,
    reject,
    request,
    requestUrl,
    resolve,
    response = {},
    success404,
    transform,
}: HandleResponseParams<ResponsePayload, ReturnedPayload>) => {
    /* eslint-disable-next-line */
    const rejectAndCaptureError = (errorToReturn: any, originalError = null) => {
        // We can capture the error here if we want to send the data to some future logging service
        // captureError(originalError || errorToReturn);
        return reject(errorToReturn);
    };

    const handleRequestError = () => {
        let errorMessage = '';
        try {
            // Support both request.url (for backward compatibility) and requestUrl
            const url = request?.url || requestUrl;

            // Format error in a meaningful way (Error objects don't JSON.stringify well)
            let errorStr = '';
            if (err instanceof Error) {
                errorStr = err.message;
                if ((err as any).status) {
                    errorStr += ` (status: ${(err as any).status})`;
                }
            } else if (typeof err === 'string') {
                errorStr = err;
            } else {
                try {
                    errorStr = JSON.stringify(err);
                } catch {
                    errorStr = String(err);
                }
            }

            errorMessage = 'Here is the error with the request: ' + errorStr + ' when making requests to: ' + url;
        } catch {
            errorMessage = 'Failed to stringify the error from the request.';
        }
        return rejectAndCaptureError(errorMessage);
    };

    // If there is a server level error, reject the promise
    if (err) {
        if (manuallyHandledErrorCodes?.includes(err.status)) {
            return reject(err.status);
        }
        if (success404 && err.status === 404) {
            return resolve(response.body || {});
        }
        // If there is not a  response, we can handle the error now. (CORS errors look like this)
        if (isEmpty(response)) {
            return handleRequestError();
        }
    }

    let body = response.body || {};
    if (transform) {
        body = transform(body);
    }

    // Go ResponseEnvelope
    if ('error' in body && body.error) {
        // Special case with clerk commands where "currentState" is in meta on failure
        if (body?.meta?.currentState) {
            return rejectAndCaptureError(body, body.payload);
        }
        return rejectAndCaptureError(body.payload);
    }

    // If the success and message properties are set in body (item-api response)
    // but success is falsy, reject the promise
    if ('success' in body && 'message' in body && !body.success) {
        return rejectAndCaptureError(body.message);
    }

    // If the success property is set in body but is falsy, reject the promise
    // Might be some sort of lambda (Flynn)
    if ('success' in body && !body.success) {
        if ('payload' in body) {
            if ('error' in body.payload) {
                // Check if the error is from the create account endpoint for the verify email screen
                if (body.payload.error === 'User Already Exists') {
                    return resolve(body);
                }
                return rejectAndCaptureError(body.payload.error);
            }
            return rejectAndCaptureError(body.payload);
        }
        return rejectAndCaptureError(body);
    }

    //if the response has an errorMessage it means it's broken
    if (body.errorMessage) {
        return rejectAndCaptureError(body.errorMessage);
    }

    if (err) {
        // If we have gotten this far and there was an err object, handle it
        return handleRequestError();
    }

    // else
    return resolve(body);
};

export const handleResponseOld = <ResponsePayload, ReturnedPayload>({
    err,
    reject,
    resolve,
    response = {},
    success404,
    transform,
}: HandleResponseParams<ResponsePayload, ReturnedPayload>) => {
    // If there is a server level error, reject the promise
    if (err) {
        if (success404 && err.status === 404) {
            // dante gets to here and needs to be transformed.  look at how I do this in hardboiled - blake 2019-09-26
            return resolve(response.body || {});
        } else if (response?.body?.error && response?.body?.payload === 'User Already Exists') {
            // the server correctly responds with a 500 when the user exists
            return reject(response.body.payload);
        }
        return reject(err);
    }

    let body = response.body || {};
    if (transform) {
        body = transform(body);
    }

    // I saw a case where transform returned an undefined
    if (!body) {
        return reject('Unexpected error transforming response');
    }

    // If the success and message properties are set in body (item-api response)
    // but success is falsy, reject the promise
    if ('success' in body && 'message' in body && !body.success) {
        if (body?.error === 'Failed to authenticate a') {
            return reject(body.error);
        }
        return reject(body.message);
    }

    if ('success' in body && 'payload' in body && !body.success) {
        return reject(body.payload);
    }

    // Go ResponseEnvelope
    if ('error' in body && body.error) {
        if ('payload' in body) {
            // Proper go response is to have the payload be an error string when the error property is true
            if (typeof body.payload === 'string') {
                return reject(body.payload);
            }
            if (typeof body.payload === 'object') {
                // The createuser endpoint in flynn returns a non-standard object when a bidder already exists
                if (body.payload.error === 'User Already Exists') {
                    return reject(body.payload.error);
                }
            }
        }
        // If the response is built correctly, the payload would have been a string and already rejected
        // but to keep the code backward compatible, we will reject with the payload anyway
        return reject(body.payload);
    }

    // If the success property is set in body but is falsy, reject the promise
    // Might be some sort of lambda (Flynn)
    if ('success' in body && !body.success) {
        if ('payload' in body) {
            if ('error' in body.payload) {
                return reject(body.payload.error);
            }
            return reject(body.payload);
        }
        return reject(body);
    }

    //if the response has an errorMessage it means it's broken
    if (body.errorMessage) {
        return reject(body.errorMessage);
    }
    // else
    return resolve(body);
};

export const handleResponseUseItemApiError = <ResponsePayload, ReturnedPayload>({
    err,
    reject,
    resolve,
    response = {},
    success404,
    transform,
}: HandleResponseParams<ResponsePayload, ReturnedPayload>) => {
    // If there is a server level error, reject the promise
    if (err) {
        if (success404 && err.status === 404) {
            resolve(response.body || {});
        }
        return reject(err);
    }

    let body = response.body || {};
    if (transform) {
        body = transform(body);
    }

    // If the successa and message properties are set in body (item-api response)
    // but success is falsy, reject the promise
    if ('success' in body && 'error' in body && !body.success) {
        return reject(body.error);
    }

    // If the success property is set in body but is falsy, reject the promise
    // Might be some sort of lambda (Flynn)
    if ('success' in body && !body.success) {
        return reject(body);
    }

    // else
    return resolve(body);
};

const addCacheKeyToUrl = (url: string) => {
    const urlObj = new URL(url);
    urlObj.searchParams.append('c', cachekey);
    return urlObj.toString();
};

/* eslint-disable typescript-sort-keys/string-enum */
export enum ApiHosts {
    // remove -DEPLOYMENT for production release
    MarketPlace = 'https://api-DEPLOYMENT.liveauctioneers.com/marketplace/APIPATH',
    Payment = 'https://api-DEPLOYMENT.liveauctioneers.com/payment/APIPATH',

    // -DEPLOYMENT becomes -barako/-stage/-preprod/-prod
    Approval = 'https://approval-DEPLOYMENT.liveauctioneers.com/approval/APIPATH',
    Analytics = 'https://analytics-service-DEPLOYMENT.liveauctioneers.com/APIPATH',
    Announcements = 'https://api-DEPLOYMENT.liveauctioneers.com/announcement-api/APIPATH',
    AuctionEngine = 'https://bidder-react-DEPLOYMENT.liveauctioneers.com/APIPATH',
    AuctionService = 'https://auction-service-DEPLOYMENT.liveauctioneers.com/APIPATH',
    Authentication = 'https://authentic-DEPLOYMENT.liveauctioneers.com/APIPATH',
    Billing = 'https://billing-DEPLOYMENT.liveauctioneers.com/APIPATH',
    CatalogManagement = 'https://catalog-management-DEPLOYMENT.liveauctioneers.com/catalog/APIPATH',
    // For API calls to the catalog-management CMS API
    // (e.g., https://catalog-management-DEPLOYMENT.liveauctioneers.com/cms/catalogs/near-me)
    CatalogManagementCMS = 'https://catalog-management-DEPLOYMENT.liveauctioneers.com/cms/catalogs/APIPATH',
    Category = 'https://category-api-DEPLOYMENT.liveauctioneers.com/category-api/APIPATH',
    Collection = 'https://api.liveauctioneers.com/collections/APIPATH/DEPLOYMENT',
    Crows = 'https://saveditem-crows-DEPLOYMENT.liveauctioneers.com/services/saveditem-crows/APIPATH',
    FeatureFlags = 'https://feature-flag-bridge-DEPLOYMENT.liveauctioneers.com/api/v1/APIPATH',
    FlynnUser = 'https://flynn-DEPLOYMENT.liveauctioneers.com/user/APIPATH',
    FpApi = 'https://api.liveauctioneers.com/fp-DEPLOYMENT/APIPATH',
    ItemApi = 'https://item-api-DEPLOYMENT.liveauctioneers.com/APIPATH',
    ItemModel = 'https://catalog-bidder-content-DEPLOYMENT.liveauctioneers.com/content/APIPATH',
    LegacySavedSearch = 'https://api.liveauctioneers.com/savedsearch/APIPATH/DEPLOYMENT',
    Mainhost = 'https://mainhost-DEPLOYMENT.liveauctioneers.com/APIPATH',
    MarketingReports = 'https://api-DEPLOYMENT.liveauctioneers.com/marketing-reports/APIPATH',
    Messaging = 'https://api-DEPLOYMENT.liveauctioneers.com/messaging/APIPATH',
    MessagingAttachments = 'https://messaging-api-DEPLOYMENT.liveauctioneers.com/messaging/APIPATH',
    Notifications = 'https://telegram-DEPLOYMENT.liveauctioneers.com/APIPATH',
    Personalization = 'https://api-DEPLOYMENT.liveauctioneers.com/personalization/APIPATH',
    Personalize = 'https://api-DEPLOYMENT.liveauctioneers.com/personalize/recommend/APIPATH',
    PubNub = 'https://ps.pndsn.com/APIPATH',
    Redstripe = 'https://redstripe-DEPLOYMENT.liveauctioneers.com/payment-api/APIPATH',
    Review = 'https://review-service-DEPLOYMENT.liveauctioneers.com/review/APIPATH',
    SavedSearch = 'https://api-DEPLOYMENT.liveauctioneers.com/savedsearch/APIPATH',
    Search = 'https://search-party-DEPLOYMENT.liveauctioneers.com/search/APIPATH',
    SemLedger = 'https://sem-ENV.liveauctioneers.com',
    Shipping = 'https://shipping-api-DEPLOYMENT.liveauctioneers.com/shipping/APIPATH',
    Stats = 'https://crispy-pancake-DEPLOYMENT.liveauctioneers.com/APIPATH',
    StreamManager = 'https://api.liveauctioneers.com/stream/APIPATH/DEPLOYMENT',
    Taxes = 'https://billing-DEPLOYMENT.liveauctioneers.com/taxes/APIPATH',
    ThirdPartyAuth = 'https://third-party-auth-DEPLOYMENT.liveauctioneers.com/APIPATH',
    Title = 'https://api-DEPLOYMENT.liveauctioneers.com/title/APIPATH',
    Validation = 'https://validation-DEPLOYMENT.liveauctioneers.com/APIPATH',
}
/* eslint-enable typescript-sort-keys/string-enum */

const dropDeploymentReplacementForProductionHosts = [ApiHosts.MarketPlace];

export const buildUrl = (hostPath: ApiHosts, deployment: string = '', apiPath: string = '') => {
    // convert the incoming path to a string so that we can edit it
    let updatedPath: string = hostPath;
    let url: string = '';

    if (deployment === 'prod' && dropDeploymentReplacementForProductionHosts.includes(hostPath)) {
        if (updatedPath.indexOf('-DEPLOYMENT') !== -1) {
            url = updatedPath.replace('-DEPLOYMENT', '');
        }
    } else {
        url = updatedPath.replace('DEPLOYMENT', deployment);
    }

    // Upgrade auction-service api call prefix to 'www' in production
    if (updatedPath === ApiHosts.AuctionEngine && deployment === 'prod') {
        const subdomain = new URL(url).hostname.split('.')[0];
        url = url.replace(subdomain, 'www');
    }

    // Upgrade item-model-api/saved-items-api api call prefix to 'www' in production
    if (updatedPath === ApiHosts.ItemModel && deployment === 'prod' && process.env.NODE_ENV !== 'development') {
        url = updatedPath.replace('catalog-bidder-content-DEPLOYMENT', 'www');
    }

    updatedPath = updatedPath.replace(updatedPath, url);
    updatedPath = updatedPath.replace('APIPATH', apiPath);

    return updatedPath;
};

type MakeRequest = {
    apiPath?: string;
    authToken?: string;
    deployment?: string;
    forceCredentials?: boolean;
    ipAddress?: string;
    path: ApiHosts;
    queryParams?: any;
    useCacheKey?: boolean;
};

type MakeRequestPrivate = MakeRequest & {
    body?: any;
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
};

/**
 * FetchRequest - A wrapper around fetch to maintain backward compatibility with superagent API
 * This allows existing code that uses .send() and .end() to work without changes
 */
class FetchRequest {
    private config: MakeRequestPrivate;
    private requestBody?: any;
    private additionalQueryParams: Record<string, any> = {};
    private formFields: Record<string, any> = {};
    private fileAttachments: Array<{ field: string; file: any }> = [];
    private contentType?: string;
    public url: string;

    constructor(config: MakeRequestPrivate) {
        this.config = config;
        // Build the URL so it's accessible for error handling
        this.url = buildUrl(config.path, config.deployment, config.apiPath);
    }

    /**
     * Set the request body (for POST, PUT, PATCH, DELETE)
     * Body is optional - calling send() without arguments is valid for empty POST requests
     *
     * Note: In some legacy code, .send() is awaited directly. To support this,
     * when send() is the last method called (not followed by .end()), it returns
     * a thenable object.
     */
    send(body?: any): this | Promise<any> {
        if (body !== undefined) {
            this.requestBody = body;
        }
        // Return this for method chaining - the FetchRequest itself is thenable
        return this;
    }

    /**
     * Add query parameters to the request
     * This matches superagent's .query() method for backward compatibility
     */
    query(params: Record<string, any>): FetchRequest {
        this.additionalQueryParams = { ...this.additionalQueryParams, ...params };
        return this;
    }

    /**
     * Add a form field (for multipart/form-data or url-encoded requests)
     * This matches superagent's .field() method for backward compatibility
     */
    field(name: string, value: any): FetchRequest {
        this.formFields[name] = value;
        return this;
    }

    /**
     * Attach a file to the request (for multipart/form-data uploads)
     * This matches superagent's .attach() method for backward compatibility
     * @param field - The field name for the file
     * @param file - The file to attach (File, Blob, or Buffer)
     */
    attach(field: string, file: any): FetchRequest {
        this.fileAttachments.push({ field, file });
        return this;
    }

    /**
     * Set the Content-Type header
     * This matches superagent's .type() method for backward compatibility
     * @param type - Either a shorthand ('json', 'form', 'xml', 'text') or a full mime type
     */
    type(type: string): FetchRequest {
        const contentTypeMap: Record<string, string> = {
            form: 'application/x-www-form-urlencoded',
            json: 'application/json',
            text: 'text/plain',
            xml: 'application/xml',
        };

        this.contentType = contentTypeMap[type] || type;
        return this;
    }

    /**
     * Execute the request and call the callback with error and response
     */
    end(callback: (err: any, response: any) => void): void {
        this.execute()
            .then((response) => callback(null, response))
            .catch((err) => {
                // Pass both error and response (if available) for proper error handling
                // This matches superagent behavior where response is available even on HTTP errors
                const response = err.response || {};
                callback(err, response);
            });
    }

    /**
     * Make the request thenable (can be awaited)
     * This matches superagent's behavior where the request itself is awaitable
     */
    then<TResult1 = any, TResult2 = never>(
        onfulfilled?: ((value: any) => TResult1 | PromiseLike<TResult1>) | null,
        onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
    ): Promise<TResult1 | TResult2> {
        return this.execute().then(onfulfilled, onrejected);
    }

    /**
     * Make the request catchable
     * This matches superagent's behavior
     */
    catch<TResult = never>(
        onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | null
    ): Promise<any | TResult> {
        return this.execute().catch(onrejected);
    }

    /**
     * Execute the fetch request
     */
    private async execute(): Promise<any> {
        let url = buildUrl(this.config.path, this.config.deployment, this.config.apiPath);

        // Add cache key if needed
        if (this.config.useCacheKey) {
            url = addCacheKeyToUrl(url);
        }

        // Merge config query params with additional query params from .query() calls
        const allQueryParams = {
            ...(this.config.queryParams || {}),
            ...this.additionalQueryParams,
        };

        // Add query parameters
        if (Object.keys(allQueryParams).length > 0) {
            const urlObj = new URL(url);
            Object.keys(allQueryParams).forEach((key) => {
                const value = allQueryParams[key];
                if (value !== undefined && value !== null) {
                    if (Array.isArray(value)) {
                        value.forEach((v) => urlObj.searchParams.append(key, String(v)));
                    } else {
                        urlObj.searchParams.append(key, String(value));
                    }
                }
            });
            url = urlObj.toString();
        }

        // Build headers
        const headers: Record<string, string> = {};

        // Set user agent for SSR
        if (typeof window === 'undefined') {
            headers['User-Agent'] = 'la-fetch';
        }

        // Add authorization header
        if (this.config.authToken) {
            headers['Authorization'] = `Bearer ${this.config.authToken}`;
        }

        // Add IP forwarding for SSR
        if (this.config.ipAddress && typeof window === 'undefined') {
            headers['X-Forwarded-For'] = this.config.ipAddress;
        }

        // Determine if we need credentials
        const shouldIncludeCredentials =
            (this.config.path &&
                (this.config.path === ApiHosts.ItemApi ||
                    this.config.path.includes('s_msg') ||
                    this.config.path.includes('eoa_removal_response'))) ||
            this.config.forceCredentials;

        // Build fetch options
        const fetchOptions: {
            body?: string | FormData;
            credentials: 'include' | 'omit' | 'same-origin';
            headers: Record<string, string>;
            method: string;
        } = {
            credentials: shouldIncludeCredentials ? 'include' : 'same-origin',
            headers,
            method: this.config.method,
        };

        // Add body for non-GET requests
        if (this.config.method !== 'GET') {
            // If files were attached with .attach(), send as multipart/form-data
            if (this.fileAttachments.length > 0) {
                const formData = new FormData();
                // Add form fields
                Object.keys(this.formFields).forEach((key) => {
                    formData.append(key, String(this.formFields[key]));
                });
                // Add file attachments
                this.fileAttachments.forEach(({ field, file }) => {
                    formData.append(field, file);
                });
                // Don't set Content-Type header - browser will set it with boundary for multipart/form-data
                fetchOptions.body = formData;
            }
            // If form fields were added with .field() (but no files), send as URL-encoded form data
            else if (Object.keys(this.formFields).length > 0) {
                const formData = new URLSearchParams();
                Object.keys(this.formFields).forEach((key) => {
                    formData.append(key, String(this.formFields[key]));
                });
                headers['Content-Type'] = this.contentType || 'application/x-www-form-urlencoded';
                fetchOptions.body = formData.toString();
            }
            // Otherwise, send the request body (if provided)
            else if (this.requestBody) {
                // If .type() was called explicitly, use that content type
                if (this.contentType) {
                    headers['Content-Type'] = this.contentType;
                    // For form-urlencoded, convert object to URLSearchParams
                    if (this.contentType === 'application/x-www-form-urlencoded') {
                        const formData = new URLSearchParams();
                        Object.keys(this.requestBody).forEach((key) => {
                            formData.append(key, String(this.requestBody[key]));
                        });
                        fetchOptions.body = formData.toString();
                    } else {
                        // For other types, send as JSON
                        fetchOptions.body = JSON.stringify(this.requestBody);
                    }
                } else {
                    // Default to JSON if no explicit type was set
                    headers['Content-Type'] = 'application/json';
                    fetchOptions.body = JSON.stringify(this.requestBody);
                }
            }
        }

        // Performance logging hooks
        if (perfLoggerRequestHooks) {
            const { onBuildRequest, onHandleResponse, onSendRequest } = perfLoggerRequestHooks;
            const requestInfo = { headers, method: this.config.method, url };
            onBuildRequest(requestInfo);
            onSendRequest(requestInfo, { url });

            try {
                const response = await fetch(url, fetchOptions);
                const body = await this.parseResponse(response);

                // Convert Headers object to plain object for backward compatibility with superagent
                const responseHeaders: Record<string, string> = {};
                response.headers.forEach((value, key) => {
                    responseHeaders[key] = value;
                });

                const formattedResponse = { body, headers: responseHeaders, ok: response.ok, status: response.status };

                // If the response is not ok, create an error
                if (!response.ok) {
                    const error = new Error(`Request failed with status ${response.status}`);
                    (error as any).status = response.status;
                    (error as any).response = formattedResponse;
                    onHandleResponse(requestInfo, formattedResponse, error);
                    throw error;
                }

                onHandleResponse(requestInfo, formattedResponse, null);
                return formattedResponse;
            } catch (error) {
                onHandleResponse(requestInfo, null, error);
                throw error;
            }
        }

        try {
            const response = await fetch(url, fetchOptions);
            const body = await this.parseResponse(response);

            // Convert Headers object to plain object for backward compatibility with superagent
            const responseHeaders: Record<string, string> = {};
            response.headers.forEach((value, key) => {
                responseHeaders[key] = value;
            });

            // Create a response object similar to superagent
            const result = { body, headers: responseHeaders, ok: response.ok, status: response.status };

            // If the response is not ok (status not in 200-299 range), create an error
            if (!response.ok) {
                const error = new Error(`Request failed with status ${response.status}`);
                (error as any).status = response.status;
                (error as any).response = result;
                throw error;
            }

            return result;
        } catch (error) {
            // If error already has response attached (HTTP error), just rethrow
            if ((error as any).response) {
                throw error;
            }

            // For network errors or other issues, create a meaningful error
            const networkError = error instanceof Error ? error : new Error(String(error));
            (networkError as any).message = `Network error: ${networkError.message || 'Failed to fetch'}`;
            (networkError as any).url = url;
            throw networkError;
        }
    }

    /**
     * Parse the response body as JSON
     */
    private async parseResponse(response: Response): Promise<any> {
        try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const json = await response.json();
                return json;
            }
            const text = await response.text();
            if (!text) {
                return {};
            }
            try {
                return JSON.parse(text);
            } catch {
                return text;
            }
        } catch (error) {
            // Log detailed error information including endpoint and response status
            // Format as JSON string for Scalyr compatibility
            console.error(
                JSON.stringify({
                    endpoint: this.url,
                    error: error instanceof Error ? error.message : String(error),
                    message: 'Failed to parse response from API call',
                    method: this.config.method,
                    status: response.status,
                    statusText: response.statusText,
                })
            );

            // Return empty object to prevent undefined errors and maintain backward compatibility
            return {};
        }
    }
}

const buildRequest = (config: MakeRequestPrivate): FetchRequest => {
    return new FetchRequest(config);
};

export const makeDelete = ({
    apiPath,
    authToken,
    deployment,
    forceCredentials,
    ipAddress,
    path,
    queryParams,
    useCacheKey = true,
}: MakeRequest): FetchRequest =>
    buildRequest({
        apiPath,
        authToken,
        deployment,
        forceCredentials,
        ipAddress,
        method: 'DELETE',
        path,
        queryParams,
        useCacheKey,
    });

export const makeGet = ({
    apiPath,
    authToken,
    deployment,
    forceCredentials,
    ipAddress,
    path,
    queryParams,
    useCacheKey = true,
}: MakeRequest): FetchRequest =>
    buildRequest({
        apiPath,
        authToken,
        deployment,
        forceCredentials,
        ipAddress,
        method: 'GET',
        path,
        queryParams,
        useCacheKey,
    });

export const makePatch = ({
    apiPath,
    authToken,
    deployment,
    forceCredentials,
    ipAddress,
    path,
    queryParams,
    useCacheKey = true,
}: MakeRequest): FetchRequest =>
    buildRequest({
        apiPath,
        authToken,
        deployment,
        forceCredentials,
        ipAddress,
        method: 'PATCH',
        path,
        queryParams,
        useCacheKey,
    });

export const makePost = ({
    apiPath,
    authToken,
    deployment,
    forceCredentials,
    ipAddress,
    path,
    queryParams,
    useCacheKey = true,
}: MakeRequest): FetchRequest =>
    buildRequest({
        apiPath,
        authToken,
        deployment,
        forceCredentials,
        ipAddress,
        method: 'POST',
        path,
        queryParams,
        useCacheKey,
    });

export const makePut = ({
    apiPath,
    authToken,
    deployment,
    forceCredentials,
    ipAddress,
    path,
    queryParams,
    useCacheKey = true,
}: MakeRequest): FetchRequest =>
    buildRequest({
        apiPath,
        authToken,
        deployment,
        forceCredentials,
        ipAddress,
        method: 'PUT',
        path,
        queryParams,
        useCacheKey,
    });

type SetGlobalRequestHooksParams = {
    onBuildRequest: Function;
    onHandleResponse: Function;
    onSendRequest: Function;
};

export const setGlobalPerfLoggerRequestHooks = ({
    onBuildRequest,
    onHandleResponse,
    onSendRequest,
}: SetGlobalRequestHooksParams) => {
    perfLoggerRequestHooks = {
        onBuildRequest,
        onHandleResponse,
        onSendRequest,
    };
};
